import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getN8nStatus, cloneN8nWorkflow, getFactoryDiagnosis, getN8nWorkflows, recloneBotWorkflow } from '@/lib/api';
import { Cpu, Network, Power, Server, Play, AlertCircle, PlusCircle, CheckCircle, Activity, ShieldAlert, History } from 'lucide-react';

const BrainFactoryPage = () => {
  const [n8nStatus, setN8nStatus] = useState({ state: 'checking', text: 'Conectando con Fábrica...' });
  const [loading, setLoading] = useState(false);
  const [prospectos, setProspectos] = useState([]);
  const [diagnosis, setDiagnosis] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [activeTab, setActiveTab] = useState('health');
  
  const [formData, setFormData] = useState({
    tenant_name: '',
    tenant_id: '',
    template_workflow_id: 'e2kaglJtlBQaU3xG' // Master V1.1
  });

  useEffect(() => {
    checkN8nStatus();
    fetchProspectosParaOnboarding();
    fetchDiagnosis();
    fetchWorkflows();
  }, []);

  const checkN8nStatus = async () => {
    try {
      const response = await getN8nStatus();
      const data = response.data;
      if (data.status === 'connected') setN8nStatus({ state: 'online', text: 'Fábrica en Línea' });
      else if (data.status === 'warning') setN8nStatus({ state: 'warning', text: 'Error de API Key' });
      else setN8nStatus({ state: 'offline', text: 'Fábrica Desconectada' });
    } catch (err) {
      setN8nStatus({ state: 'offline', text: 'Fábrica Desconectada' });
    }
  };

  const fetchProspectosParaOnboarding = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/prospectos`);
      const data = await response.json();
      const ganados = data.filter(p => p.estado === 'won');
      setProspectos(ganados);
    } catch (err) {
      console.error('Error fetching prospectos', err);
    }
  };

  const fetchDiagnosis = async () => {
    try {
      const response = await getFactoryDiagnosis();
      setDiagnosis(response.data);
    } catch (err) {
      console.error('Error fetching diagnosis', err);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await getN8nWorkflows();
      setWorkflows(response.data.workflows || []);
    } catch (err) {
      console.error('Error fetching workflows', err);
    }
  };

  const handleRepairBot = async (botId) => {
    setLoading(true);
    const promise = recloneBotWorkflow(botId);

    toast.promise(promise, {
      loading: 'Reparando cerebro y reconectando flujos...',
      success: '¡Bot Reparado! El nuevo workflow ya está activo.',
      error: 'Error al intentar reparar el bot.',
    });

    try {
      await promise;
      fetchDiagnosis();
      fetchWorkflows();
    } finally {
      setLoading(false);
    }
  };

  const handleManualCreation = async (e) => {
    e.preventDefault();
    if (!formData.tenant_id || !formData.tenant_name || n8nStatus.state !== 'online') {
      toast.error("Datos incompletos o Fábrica no conectada.");
      return;
    }

    setLoading(true);
    const promise = cloneN8nWorkflow({
      tenant_id: formData.tenant_id,
      tenant_name: formData.tenant_name,
      template_workflow_id: formData.template_workflow_id
    });

    toast.promise(promise, {
      loading: 'Clonando flujos de n8n y forjando cerebros...',
      success: (res) => {
        fetchDiagnosis();
        fetchWorkflows();
        return `¡Bot creado y encendido! ID: ${res.data.n8n_workflow_id}`;
      },
      error: 'Error al forjar tu nuevo bot.',
    });

    try {
      await promise;
      setFormData({ tenant_name: '', tenant_id: '', template_workflow_id: 'e2kaglJtlBQaU3xG' });
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Sano</Badge>;
      case 'missing_workflow': return <Badge variant="destructive">Sin Flujo</Badge>;
      case 'broken_link': return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Enlace Roto</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-8" data-testid="brain-factory-page">
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Network className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Manufactura Cognitiva</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Brain Factory</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Despliegue y monitoreo de núcleos de inteligencia n8n para bots operativos</p>
        </motion.div>

        <div className="flex items-center gap-3 bg-[#0F172A]/60 backdrop-blur-xl p-3 px-5 rounded-[2rem] border border-white/5 shadow-2xl shadow-black/40 group overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <div className="flex items-center gap-3 pr-4 border-r border-white/10 relative z-10">
            <div className={`w-3 h-3 rounded-full ${n8nStatus.state === 'online' ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]' : 'bg-red-500 animate-pulse shadow-[0_0_12px_rgba(239,68,68,0.4)]'}`} />
            <span className="font-bold text-xs uppercase tracking-widest text-slate-300">{n8nStatus.text}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { checkN8nStatus(); fetchDiagnosis(); fetchWorkflows(); }} 
            className="h-9 hover:bg-white/5 text-slate-400 hover:text-white transition-all rounded-xl relative z-10"
          >
            <Activity className="w-4 h-4 mr-2" /> 
            <span className="text-[10px] font-black uppercase tracking-widest">Sincronizar</span>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="health" className="w-full" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex items-center gap-2 p-1 bg-slate-900/40 border border-white/5 rounded-2xl w-fit mb-8">
          <TabsTrigger 
            value="health" 
            className="rounded-xl px-6 py-2.5 font-bold text-xs transition-all duration-300 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/20"
          >
            <ShieldAlert className="w-4 h-4 mr-2" /> Salud & Flujos
          </TabsTrigger>
          <TabsTrigger 
            value="onboarding" 
            className="rounded-xl px-6 py-2.5 font-bold text-xs transition-all duration-300 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-emerald-500/20"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Onboarding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="onboarding" className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* MANUAL CREATION PANEL */}
            <Card className="glass-card border-primary/20">
              <CardHeader>
                <CardTitle>Inyección Maestras</CardTitle>
                <CardDescription>Crea un nuevo Tenant inyectándole flujos de cero de forma forzada.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleManualCreation} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre Comercial</Label>
                      <Input 
                        placeholder="Ej: Panadería El Sol" 
                        value={formData.tenant_name}
                        onChange={(e) => setFormData({...formData, tenant_name: e.target.value, tenant_id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tenant ID</Label>
                      <Input readOnly className="bg-secondary/50 font-mono text-sm" value={formData.tenant_id} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>ID del Workflow Template n8n</Label>
                    <Input 
                      placeholder="Ej: e2kaglJtlBQaU3xG" 
                      value={formData.template_workflow_id}
                      onChange={(e) => setFormData({...formData, template_workflow_id: e.target.value})}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="group relative overflow-hidden rounded-2xl bg-blue-600 hover:bg-blue-500 text-white w-full h-14 shadow-xl shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/30 hover:scale-[1.01]" 
                    disabled={loading || n8nStatus.state !== 'online'}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/10 to-blue-400/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    {loading ? (
                      <span className="loading-spinner w-5 h-5 relative z-10"></span>
                    ) : (
                      <div className="flex items-center justify-center gap-3 relative z-10">
                        <Power className="w-5 h-5 text-white" />
                        <span className="font-bold uppercase tracking-widest text-xs">Crear e Inyectar Workflow</span>
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* PROSPECTOS PIPELINE PANEL */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Onboarding CRM</CardTitle>
                <CardDescription>Prospectos "Won" esperando activación de sus agentes.</CardDescription>
              </CardHeader>
              <CardContent>
                {prospectos.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border border-dashed rounded-lg border-border">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-10" />
                    <p>Todo está al día.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                    {prospectos.map((p, idx) => (
                      <div key={idx} className="p-4 rounded-xl border border-border bg-secondary/10 flex justify-between items-center transition-hover hover:border-primary/40">
                        <div>
                          <h4 className="font-bold text-lg">{p.empresa}</h4>
                          <p className="text-xs text-muted-foreground">{p.contacto_nombre}</p>
                        </div>
                        <Button size="sm" onClick={() => setFormData({tenant_name: p.empresa, tenant_id: p.empresa.toLowerCase().replace(/[^a-z0-9]/g, ''), template_workflow_id: 'e2kaglJtlBQaU3xG'})} variant="ghost" className="text-primary">
                          <PlusCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="health" className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* DIAGNOSIS SYSTEM */}
            <Card className="lg:col-span-2 glass-card border-amber-500/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Diagnóstico de Enlace (Tenant ↔ Cerebro)</CardTitle>
                  <CardDescription>Verificación de la integridad de los flujos de n8n por bot.</CardDescription>
                </div>
                {diagnosis && (
                    <div className="text-right">
                      <div className="text-2xl font-bold flex items-center justify-end gap-1">
                        {diagnosis?.summary?.healthy || 0}
                        <span className="text-muted-foreground/50 text-xl">/</span>
                        <span className="text-muted-foreground/30">{diagnosis?.summary?.total || 0}</span>
                      </div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground/50">Bots Sanos</p>
                    </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="rounded-xl border border-border overflow-hidden bg-background/50">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/50 text-muted-foreground text-left">
                      <tr>
                        <th className="p-3 font-medium">Bot / Proyecto</th>
                        <th className="p-3 font-medium">Workflow ID</th>
                        <th className="p-3 font-medium text-center">Estado</th>
                        <th className="p-3 font-medium">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {diagnosis?.details.map((d, idx) => (
                        <tr key={idx} className="hover:bg-primary/5 transition-colors">
                          <td className="p-3">
                            <p className="font-bold">{d.bot_name}</p>
                            <p className="text-xs text-muted-foreground">{d.tenant_name}</p>
                          </td>
                          <td className="p-3 font-mono text-xs">{d.workflow_id || 'N/A'}</td>
                          <td className="p-3 text-center"><StatusBadge status={d.status} /></td>
                          <td className="p-3">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className={`h-8 w-8 ${d.status !== 'healthy' ? 'text-amber-500 hover:text-amber-600 animate-pulse' : 'text-muted-foreground opacity-50'}`}
                              onClick={() => handleRepairBot(d.bot_id)}
                              disabled={loading || d.status === 'healthy'}
                              title={d.status === 'healthy' ? "El bot está sano" : "Reparar enlace con n8n"}
                            >
                               <History className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* LIVE N8N WORKFLOWS LIST */}
            <Card className="glass-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                   <History className="w-5 h-5 text-primary" />
                   <CardTitle>Flujos Activos en n8n</CardTitle>
                </div>
                <CardDescription>Lista real de workflows detectados en el servidor.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                   {workflows.map((wf) => (
                     <div key={wf.id} className="p-3 rounded-lg border border-border bg-background/30 flex justify-between items-center text-xs">
                        <div className="truncate pr-2">
                           <p className="font-bold truncate" title={wf.name}>{wf.name}</p>
                           <p className="text-muted-foreground font-mono">{wf.id}</p>
                        </div>
                        <Badge variant={wf.active ? "success" : "outline"} className={wf.active ? "bg-green-500/10 text-green-500" : ""}>
                           {wf.active ? "ACTIVO" : "OFF"}
                        </Badge>
                     </div>
                   ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrainFactoryPage;
